import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const CompanyPage = () => {
  useSEO({
    title: '会社概要 — 静かな伴走、そっと前へ | 2b LLC',
    description: '2b LLC（トゥービー）の会社概要。東京都世田谷区を拠点に、生成AIコンサルティング、政治・政策支援、地域活性化、不動産業、秘書業代行、文章作成を展開。代表メッセージ、理念、会社情報をご紹介します。',
    keywords: '2b LLC,会社概要,企業情報,代表メッセージ,理念,東京,世田谷',
    canonical: getSiteUrl('/company'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '会社概要',
      description: '2b LLCの会社概要、代表メッセージ、理念、会社情報',
      url: getSiteUrl('/company'),
      mainEntity: {
        '@type': 'Organization',
        name: '合同会社2b',
        alternateName: '2b LLC',
        description: '静かな伴走で誰かの想いを形にする会社',
        foundingDate: '2025-02-06',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '世田谷区',
          addressRegion: '東京都',
          addressCountry: 'JP'
        },
        email: 'tobe26.2b@gmail.com',
        founder: {
          '@type': 'Person',
          name: '田崎 陽介'
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />
      
      <main className="pt-32">
        {/* ロゴセクション */}
        <section 
          className="py-12 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/12e4424d5b2aaebd545598b61604cd28.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <section className="text-center mb-16">
              <FadeUpAnimation duration={1400}>
                <img 
                  src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
                  alt="2b LLC"
                  className="w-[480px] h-auto mx-auto mb-12 object-contain"
                />
              </FadeUpAnimation>
            </section>
          </div>
        </section>

        {/* ヒーローセクション */}
        <section 
          className="py-32 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Gentle%20water%20ripples%20spreading%20across%20calm%20surface%2C%20soft%20concentric%20circles%20in%20peaceful%20blue%20water%2C%20serene%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20minimalist%20zen%20composition%20perfect%20for%20company%20hero%20section%2C%20tranquil%20and%20professional%20atmosphere&width=1920&height=1080&seq=company-hero-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
            <FadeUpAnimation duration={1400}>
              <h1 className="text-5xl md:text-6xl font-semibold text-[#4A4A48] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Company
              </h1>
            </FadeUpAnimation>
            
            <FadeUpAnimation duration={1400}>
              <p className="text-xl text-[#4A4A48] opacity-80" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                — つながり、支え、動かす。
              </p>
            </FadeUpAnimation>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6">
          {/* 代表メッセージセクション */}
          <section 
            className="bg-white relative overflow-hidden rounded-2xl mb-16"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=Gentle%20water%20ripples%20spreading%20across%20calm%20surface%2C%20soft%20concentric%20circles%20in%20peaceful%20blue%20water%2C%20serene%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20minimalist%20zen%20composition%20perfect%20for%20message%20sections%2C%20tranquil%20and%20harmonious%20atmosphere&width=1920&height=1080&seq=company-message-bg&orientation=landscape)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="absolute inset-0 bg-white/85 rounded-2xl"></div>
            <div className="p-8 relative z-10">
              <div className="bg-white/70 p-8 rounded-2xl shadow-sm max-w-4xl mx-auto">
                <FadeUpAnimation duration={1400}>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-semibold text-[#4A4A48] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      代表メッセージ
                    </h2>
                    <p className="text-base text-[#4A4A48] opacity-70" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                      to be — あるべき姿へ。
                    </p>
                  </div>
                </FadeUpAnimation>
                
                <FadeUpAnimation delay={400}>
                  <div className="space-y-4 text-[#4A4A48] leading-[1.75] text-sm md:text-xl" style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}>
                    <p>現状に不満を抱きつつも、変わる勇気を持てない人がいます。</p>
                    <p>その姿を見るとどうしても後押ししたくなるんです。</p>
                    <p>誰かの想いを形にすることが私の原動力です。</p>
                    <p>誰かが笑ってくれたら嬉しい。</p>
                    <p>その人が一歩前に進めたならば、それで十分。</p>
                    <p>そうやって少しずつ、社会は良くなっていく。それが自分の役割だと信じています。</p>
                    <p>私にとって「仕事」とは人の想いを現実にするための小さな仕組みづくりです。</p>
                    <p>動けない誰かに一歩を踏み出してもらいたい。</p>
                    <p>幸せになるのは、悪いことなんかじゃない。</p>
                    <p>生きてきて良かった、そう思える自分でいて欲しい。</p>
                    <p className="text-center font-medium pt-6 border-t border-[#C8C6C3]/40">—— to be、あるべき姿へ。</p>
                  </div>
                </FadeUpAnimation>
              </div>
            </div>
          </section>

          {/* 社名とロゴに込めた想い */}
          <section 
            className="relative overflow-hidden rounded-2xl mb-16"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=Gentle%20water%20ripples%20spreading%20across%20calm%20surface%2C%20soft%20concentric%20circles%20in%20peaceful%20blue%20water%2C%20serene%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20minimalist%20zen%20composition%20perfect%20for%20content%20sections%2C%20tranquil%20and%20harmonious%20atmosphere&width=1920&height=1080&seq=company-logo-meaning-bg&orientation=landscape)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="absolute inset-0 bg-[#F6F3EB]/80 rounded-2xl"></div>
            <div className="bg-[#F6F3EB]/50 p-12 rounded-2xl border border-[#D7D5D0]/30 relative z-10 max-w-4xl mx-auto">
              <FadeUpAnimation delay={400} duration={1400}>
                <h2
                  className="text-xl font-semibold text-[#4A4A48] mb-8 text-center pb-4 border-b border-[#C8C6C3]/40 whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  社名とロゴに込めた想い
                </h2>
              </FadeUpAnimation>
              
              <FadeUpAnimation delay={600} duration={1400}>
                <div className="space-y-4 text-[#4A4A48] leading-[1.75] text-sm md:text-xl" style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}>
                  <p>2b（トゥービー）は "to be or not to be", "as is / to be",そして "to be continued" に由来しています。</p>
                  
                  <p>今ある姿（as is）から、あるべき姿（to be）へ。<br />
                  人も組織も、地域も、そして社会も、変わり続けるものです。<br />
                  その過程に寄り添って、そっと背中を押す存在でありたいと願っています。</p>
                  
                  <p>ロゴは「静かな伴走」を象徴する波紋をモチーフにしました。<br />
                  ひとつの想いが広がって、次の誰かへつながっていく。<br />
                  その連鎖を静かな水面に描きました。</p>
                </div>
              </FadeUpAnimation>
            </div>
          </section>

          {/* Philosophy（理念） */}
          <section 
            className="relative overflow-hidden rounded-2xl mb-16"
            style={{
              backgroundImage: 'url(https://readdy.ai/api/search-image?query=Gentle%20water%20ripples%20spreading%20across%20calm%20surface%2C%20soft%20concentric%20circles%20in%20peaceful%20blue%20water%2C%20serene%20aquatic%20background%20with%20subtle%20light%20reflections%2C%20minimalist%20zen%20composition%20perfect%20for%20philosophy%20sections%2C%20tranquil%20and%20harmonious%20atmosphere&width=1920&height=1080&seq=company-philosophy-bg&orientation=landscape)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="absolute inset-0 bg-white/85 rounded-2xl"></div>
            <div className="bg-white/70 p-12 rounded-2xl shadow-sm relative z-10 max-w-4xl mx-auto">
              <FadeUpAnimation delay={200}>
                <h2
                  className="text-xl font-semibold text-[#4A4A48] mb-8 text-center pb-4 border-b border-[#C8C6C3]/40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Philosophy（理念）
                </h2>
              </FadeUpAnimation>

              <FadeUpAnimation delay={400}>
                <div>
                  <div className="space-y-4">
                    <p className="text-sm md:text-xl text-[#4A4A48] leading-[1.75] mb-4">
                      私たちは、言葉と仕組みによって"人の想い"を現実に変える会社です。
                      <br />
                      目立たずとも、確かに支える。
                      <br />
                      小さくても、確かに動かす。
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3
                        className="text-base md:text-lg font-semibold text-[#4A4A48] mb-3"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Mission（使命）
                      </h3>
                      <p
                        className="text-[#4A4A48] leading-[1.75] text-sm md:text-xl"
                        style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                      >
                        「よりよい人生を生きる」のは、権利ではなく、義務である。<br />
                        私たちは、人が自分の幸せを追求し、行動できる社会を支えます。
                      </p>
                    </div>

                    <div>
                      <h3
                        className="text-base md:text-lg font-semibold text-[#4A4A48] mb-3"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Vision（目指す姿）
                      </h3>
                      <p
                        className="text-[#4A4A48] leading-[1.75] text-sm md:text-xl"
                        style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                      >
                        静かに、確かに、前へ。<br />
                        人と社会の"あるべき姿"を見つめて、共に歩み続ける。
                      </p>
                    </div>

                    <div>
                      <h3
                        className="text-base md:text-lg font-semibold text-[#4A4A48] mb-3"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Value（価値観）
                      </h3>
                      <ul
                        className="text-[#4A4A48] leading-[1.75] space-y-1 text-sm md:text-xl"
                        style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                      >
                        <li>・和をもって貴しとなす</li>
                        <li>・決めたらやる</li>
                        <li>・属性に捉われずに人を見る</li>
                        <li>・人の気持ちを否定しない</li>
                        <li>・センシティブなことを言わない</li>
                        <li>・リスクを恐れず、責任を取る</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </FadeUpAnimation>
            </div>
          </section>

          {/* 会社情報 */}
          <div className="bg-[#F6F3EB]/50 p-12 rounded-2xl border border-[#D7D5D0]/30 relative z-10 max-w-4xl mx-auto mb-16">
            <FadeUpAnimation delay={0.4}>
              <h2 
                className="text-xl font-semibold text-[#4A4A48] mb-8 text-center pb-4 border-b border-[#C8C6C3]/40"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                会社情報
              </h2>
            </FadeUpAnimation>
            
            <FadeUpAnimation delay={0.6}>
              <div 
                className="text-[#4A4A48]"
                style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
              >
                <div className="space-y-3 mb-8">
                  <div className="flex text-sm md:text-xl">
                    <span className="font-medium">会社名：</span>
                    <span>合同会社2b（トゥービー）</span>
                  </div>
                  <div className="flex text-sm md:text-xl">
                    <span className="font-medium">設立：</span>
                    <span>2025年2月6日</span>
                  </div>
                  <div className="flex text-sm md:text-xl">
                    <span className="font-medium">所在地：</span>
                    <span>東京都世田谷区</span>
                  </div>
                  <div className="flex text-sm md:text-xl">
                    <span className="font-medium">代表社員：</span>
                    <span>田崎 陽介</span>
                  </div>
                  <div className="flex text-sm md:text-xl">
                    <span className="font-medium">メール：</span>
                    <span>tobe26.2b@gmail.com</span>
                  </div>
                </div>
                <div>
                  <div className="font-medium mb-3 text-sm md:text-xl">事業内容：</div>
                  <ul className="space-y-1 pl-4 text-sm md:text-xl">
                    <li>・生成AIコンサルティング</li>
                    <li>・政治・政策支援</li>
                    <li>・地域活性化</li>
                    <li>・不動産業</li>
                    <li>・秘書業代行</li>
                    <li>・文章作成</li>
                  </ul>
                </div>
              </div>
            </FadeUpAnimation>
          </div>

          {/* CTAセクション */}
          <section className="mt-16">
            <FadeUpAnimation delay={1600} duration={1400}>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a 
                  href="/philosophy"
                  className="inline-flex items-center px-8 py-3 bg-[#AFC5C2] text-white rounded-lg hover:bg-[#9BB5B2] transition-all duration-300 font-medium whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}
                >
                  理念ページはこちら
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="/services"
                  className="inline-flex items-center px-8 py-3 bg-[#4A4A48] text-white rounded-lg hover:bg-[#5B5A57] transition-all duration-300 font-medium whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Zen Kaku Gothic Soft, sans-serif' }}
                >
                  サービス一覧はこちら
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </FadeUpAnimation>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CompanyPage;
